import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useRouteHelpers } from "routes/helpers";
import routes from "routes/routes";
import { useAppSelector } from "store/hooks";
import { companyMetadataSelector, userMetadataSelector } from "store/selectors";

import { EMagicLinkFlow } from "types/AuthFlowTypes";
import { ErrorConst, MFAFactorType } from "types/BETypes";
import { IUserWithCompany } from "types/user";
import { COMPANIES_LIST_FETCH_ALL_LIMIT } from "constants/company/companiesList";
import { showErrorModal } from "helpers";
import { hasUserMultipleCompanies } from "helpers/auth/company";
import useAuth from "hooks/useAuth";
import { useCompaniesList } from "hooks/useCompaniesList";
import { ISignInMfaPageNavigationParams } from "pages/Auth/SignInMfaPage/types";
import { ISignUpPageLocationState } from "pages/Auth/SignUpPage/types";

import {
  ListUserCompaniesResDto,
  queryAuthControllerListMfaFactors,
} from "utils/swagger_react_query";

import { IAuthSignInSearchParams } from "../types";

interface IParams {
  mode: EMagicLinkFlow;
  autoLogin?: boolean;
}

type ICheckUserDataResult = IUserWithCompany & {
  companiesList: ListUserCompaniesResDto | undefined;
};

export const useCompleteSignIn = ({ mode, autoLogin = true }: IParams) => {
  const isCompletePath = [
    routes.SIGN_UP_COMPLETE,
    routes.SIGN_IN_COMPLETE,
    routes.EMPLOYEE_SIGN_UP,
    routes.ADMIN_SIGN_UP,
  ].includes(location.pathname as routes);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const locationState = useLocation().state as ISignUpPageLocationState;
  const currentUser = useAppSelector(userMetadataSelector);
  const currentCompany = useAppSelector(companyMetadataSelector);

  const [isLoading, setLoading] = useState<boolean>(isCompletePath);
  const { isAuthenticated, login, logout, selectActiveCompany, getCurrentUserAndCompany } =
    useAuth();
  const { redirectByRoleAndSignUpStatus } = useRouteHelpers();
  const {
    actions: { refetchCompanies },
  } = useCompaniesList({ perPage: COMPANIES_LIST_FETCH_ALL_LIMIT });

  const getAuthParams = (): IAuthSignInSearchParams => {
    return {
      code: searchParams.get("code") || undefined,
      error: searchParams.get("error") || undefined,
      invitationId: searchParams.get("invitationId") || undefined,
      impersonateId: searchParams.get("impersonateId") || undefined,
    };
  };
  const getIsAuthParamsExist = (): boolean =>
    !!Object.values(getAuthParams()).filter((it) => it).length;

  useEffect(() => {
    const authParams = getAuthParams();
    if (isCompletePath && autoLogin) {
      if (authParams?.code || authParams?.error) {
        setLoading(true);
        completeSignIn(mode, getAuthParams());
      } else {
        checkUserData();
      }
    }

    return () => {
      searchParams.delete("code");
      searchParams.delete("error");
      searchParams.delete("invitationId");
      searchParams.delete("impersonateId");
    };
  }, []);

  const checkUserData = async (): Promise<ICheckUserDataResult | undefined> => {
    const isUserAuthenticated = isAuthenticated();
    const isAuthParamsExist = getIsAuthParamsExist();
    if (!isUserAuthenticated && !isAuthParamsExist) {
      setLoading(false);
      navigate(routes.SIGN_IN);
      return;
    }

    const companiesListRes = await refetchCompanies(undefined, true);
    const userAndCompanyDataRes = await getCurrentUserAndCompany();
    const userAndCompanyData: IUserWithCompany | undefined = {
      user: userAndCompanyDataRes?.user,
      company: userAndCompanyDataRes?.company,
    };

    const companyIds = companiesListRes?.companies?.map((it) => it.companyId) || [];
    const hasUserActiveCompany = !!userAndCompanyData?.user?.activeCompanyId;
    const shouldSkipRedirect =
      locationState?.isAdditionalCompanyCreation && mode === EMagicLinkFlow.SIGN_UP;
    const shouldRedirectToSelectCompany =
      hasUserMultipleCompanies(companyIds) && !hasUserActiveCompany && !shouldSkipRedirect;
    const shouldAutoSelectCompany =
      companiesListRes?.total && !hasUserActiveCompany && !shouldSkipRedirect;

    if (shouldRedirectToSelectCompany) {
      navigate(routes.SELECT_COMPANY);
      return;
    } else if (shouldAutoSelectCompany) {
      await selectActiveCompany(companyIds?.[0]);
      const userAndCompanyDataRes = await getCurrentUserAndCompany();
      userAndCompanyData.user = userAndCompanyDataRes?.user;
      userAndCompanyData.company = userAndCompanyDataRes?.company;
    }
    if (!shouldSkipRedirect) {
      redirectByRoleAndSignUpStatus(userAndCompanyData, companyIds);
    }
    setLoading(false);
    return {
      ...userAndCompanyData,
      companiesList: companiesListRes,
    };
  };

  const completeSignIn = async (
    mode: EMagicLinkFlow,
    authParams: IAuthSignInSearchParams,
    invitationId?: string,
  ): Promise<IUserWithCompany | undefined> => {
    try {
      if (authParams?.error) throw authParams.error;
      const authCode = !invitationId ? authParams?.code || "" : "";
      const isOwnerSignUpFlow = mode === EMagicLinkFlow.SIGN_UP && !invitationId;
      const isAuthParamsExist = getIsAuthParamsExist();
      if (!isAuthParamsExist) await checkUserData();
      const userAndCompanyData: IUserWithCompany | undefined = {
        user: null,
        company: null,
      };

      const loginRes = await login(
        authCode,
        mode,
        invitationId || undefined,
        authParams.impersonateId || undefined,
      );
      if (!loginRes?.companyIds.length) {
        // NOTE::: if user has no companies => redirect to SIGN_UP page
        redirectByRoleAndSignUpStatus(userAndCompanyData, loginRes.companyIds);
        return;
      }

      if (!authParams.impersonateId) {
        const factorsListRes = await queryAuthControllerListMfaFactors();
        const shouldShowMfa = factorsListRes.factors.find(
          (factor) => factor.type === MFAFactorType.SMS && !!factor.isVerified,
        );
        if (shouldShowMfa) {
          const locationState: ISignInMfaPageNavigationParams = {
            code: authCode,
            invitationId: invitationId,
            mode: mode,
          };
          navigate(routes.SIGN_IN_COMPLETE_MFA, {
            replace: true,
            state: locationState,
          });
          return;
        }
      }

      const shouldRedirectToSelectCompany =
        hasUserMultipleCompanies(loginRes.companyIds) || isOwnerSignUpFlow;

      if (shouldRedirectToSelectCompany) {
        navigate(routes.SELECT_COMPANY);
        return;
      } else {
        await selectActiveCompany(loginRes.companyIds?.[0]);
        const userAndCompanyDataRes = await getCurrentUserAndCompany();
        userAndCompanyData.user = userAndCompanyDataRes?.user;
        userAndCompanyData.company = userAndCompanyDataRes?.company;
      }

      redirectByRoleAndSignUpStatus(userAndCompanyData, loginRes.companyIds);
      return userAndCompanyData;
    } catch (error: any) {
      setTimeout(() => {
        if ([ErrorConst.ACCESS_DENIED].includes(error)) {
          return showErrorModal(ErrorConst.TOKEN_NOT_VERIFIED || "", () => logout(false, true));
        }

        showErrorModal(error || "", () => logout(false, true));
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    metadata: {
      isLoading,
    },
    actions: {
      checkUserData,
      completeSignIn,
      redirectByRoleAndSignUpStatus,
      setLoading,
    },
  };
};
